import { Alert, Button, Card, Input, Space, Typography } from "antd";
import type { CertificateFiles, IdCardInfo, OcrIdCardInfo } from "@zhihaoscm/entity";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { Grid as ZhGrid, GridItem as ZhGridItem } from "zhihao-ui-react";

import request, { shippingRequestHttp } from "@/api";
import type { ShipInfoRow } from "./ShipInfoPage";

export interface ShipInfoEditForm {
    type?: number;
    length?: number;
    width?: number;
    deep?: number;
    captain?: string;
    mobile?: string;
    tonTotal?: number;
    cnname?: string;
    name?: string;
    carrier?: number;
    tonCapacity?: number;
    tonPure?: number;
    remark?: string;
    /** 沿用 Vue 编辑表单的船舶资质文件字段。 */
    certificateFiles?: Partial<CertificateFiles>;
    /** 沿用 Vue 编辑表单的船长身份证识别字段。 */
    idCardInfo?: Partial<IdCardInfo>;
    /** 船员适任证书识别出的职务资格。 */
    jobQualification?: string;
}

export type UploadShipFile = (file: File) => Promise<{ id?: number } | number>;
export type RecognizeInspectionReport = (
    fileIds: number[]
) => Promise<Partial<{ len: number; wid: number; deep: number }>>;
export type RecognizeBusinessTransport = (fileId: number) => Promise<Record<string, unknown>>;
export type RecognizeIdCard = (fileId: number, type: number) => Promise<Partial<OcrIdCardInfo>>;
export type RecognizeCrewCertificate = (fileId: number) => Promise<string>;

export interface ShipInfoEditPageProps {
    loadShip?: (id: string) => Promise<ShipInfoRow>;
    updateShip?: (id: string, form: ShipInfoEditForm) => Promise<unknown>;
    bindShip?: (
        id: string,
        form: { carrier: number; tonTotal?: number; tonCapacity?: number; tonPure?: number }
    ) => Promise<unknown>;
    uploadFile?: UploadShipFile;
    recognizeInspectionReport?: RecognizeInspectionReport;
    recognizeBusinessTransport?: RecognizeBusinessTransport;
    recognizeIdCard?: RecognizeIdCard;
    recognizeCrewCertificate?: RecognizeCrewCertificate;
}

/** 沿用 Vue 船舶编辑页的详情回显接口。 */
export async function loadShipInfoEdit(id: string): Promise<ShipInfoRow> {
    const response = await shippingRequestHttp.service.get<ShipInfoRow>(`/ship/vo/${encodeURIComponent(id)}`);
    return response.data;
}

/** 沿用 Vue shipService.updateData 的编辑保存接口。 */
export async function updateShipInfo(id: string, form: ShipInfoEditForm) {
    const response = await shippingRequestHttp.service.put(`/ship/${encodeURIComponent(id)}`, form);
    return response.data;
}

/** 沿用 Vue shipService.bind 的绑定接口。 */
export async function bindShipInfo(
    id: string,
    form: { carrier: number; tonTotal?: number; tonCapacity?: number; tonPure?: number }
) {
    const response = await shippingRequestHttp.service.put(`/ship/bind/${encodeURIComponent(id)}`, form);
    return response.data;
}

/** 沿用 Vue 文件上传组件的 FormData.file 上传契约。 */
export async function uploadShipFile(file: File): Promise<{ id?: number } | number> {
    const formData = new FormData();
    formData.append("file", file);
    const response = await request.service.post<{ id?: number } | number>("/dataService/admin/file/upload", formData);
    return response.data;
}

/** 沿用 Vue 船检报告识别接口，数组参数由请求层按旧接口规则序列化。 */
export async function recognizeShipInspectionReport(fileIds: number[]) {
    const response = await shippingRequestHttp.service.get<Partial<{ len: number; wid: number; deep: number }>>(
        "/ship/recognize-ship-inspection-report",
        { params: { fileIds } }
    );
    return response.data;
}

/** 沿用 Vue 船舶营业运输证识别接口。 */
export async function recognizeShipBusinessTransport(fileId: number) {
    const response = await shippingRequestHttp.service.post<Record<string, unknown>>(
        `/ship/get-ship-business-transport?fileId=${fileId}`
    );
    return response.data;
}

/** 沿用 Vue 身份证 OCR 接口，type=1 表示正面、type=0 表示反面。 */
export async function recognizeShipIdCard(fileId: number, type: number) {
    const response = await request.service.get<Partial<OcrIdCardInfo>>(
        `/admin/ocr/id-card?fileId=${fileId}&type=${type}`
    );
    return response.data;
}

/** 沿用 Vue 船员适任证书识别接口。 */
export async function recognizeShipCrewCertificate(fileId: number) {
    const response = await shippingRequestHttp.service.post<string>(
        `/ship/recognize-crew-certificate?fileId=${fileId}`
    );
    return response.data;
}

const formFromShip = (ship?: ShipInfoRow["ship"]): ShipInfoEditForm => ({
    type: ship?.type,
    length: ship?.length,
    width: ship?.width,
    deep: ship?.deep,
    captain: ship?.captain,
    mobile: ship?.mobile,
    tonTotal: ship?.tonTotal,
    cnname: ship?.cnname,
    name: ship?.name,
    carrier: ship?.carrier,
    tonCapacity: ship?.tonCapacity,
    tonPure: ship?.tonPure,
    remark: ship?.remark,
    certificateFiles: { ...(ship?.certificateFiles || {}) },
    idCardInfo: { ...(ship?.idCardInfo || {}) },
    jobQualification: ship?.jobQualification
});

/** 船舶编辑/绑定页，按 Vue `band` 参数切换可编辑字段与提交 payload。 */
export function ShipInfoEditPage({
    loadShip = loadShipInfoEdit,
    updateShip = updateShipInfo,
    bindShip = bindShipInfo,
    uploadFile = uploadShipFile,
    recognizeInspectionReport = recognizeShipInspectionReport,
    recognizeBusinessTransport = recognizeShipBusinessTransport,
    recognizeIdCard = recognizeShipIdCard,
    recognizeCrewCertificate = recognizeShipCrewCertificate
}: ShipInfoEditPageProps) {
    const { id = "", band } = useParams<{ id: string; band?: string }>();
    const navigate = useNavigate();
    const bindMode = Boolean(band);
    const [form, setForm] = useState<ShipInfoEditForm>({});
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [recognizing, setRecognizing] = useState("");
    const formRef = useRef(form);

    useEffect(() => {
        if (!id) {
            setError("缺少船舶编号");
            setLoading(false);
            return;
        }
        let disposed = false;
        setLoading(true);
        void loadShip(id)
            .then(data => {
                if (!disposed) {
                    const nextForm = formFromShip(data.ship);
                    // 先更新 ref，兼容详情请求完成前用户已点击识别按钮的场景。
                    formRef.current = nextForm;
                    setForm(nextForm);
                }
            })
            .catch(() => {
                if (!disposed) setError("船舶信息加载失败");
            })
            .finally(() => {
                if (!disposed) setLoading(false);
            });
        return () => {
            disposed = true;
        };
    }, [id, loadShip]);

    /** 更新单个表单字段，保持表单回显值和用户修改值独立。 */
    const setField = <K extends keyof ShipInfoEditForm>(key: K, value: ShipInfoEditForm[K]) => {
        setForm(current => ({ ...current, [key]: value }));
    };

    /** 从上传接口兼容数字和带 id 对象两种 Vue 返回结构。 */
    const getUploadedFileId = (result: { id?: number } | number): number | undefined =>
        typeof result === "number" ? result : result.id;

    /** 上传船检报告并把文件 ID 保留到 Vue certificateFiles 字段。 */
    const handleInspectionUpload = async (file?: File) => {
        if (!file) return;
        setError("");
        setRecognizing("upload-inspection");
        try {
            const fileId = getUploadedFileId(await uploadFile(file));
            if (fileId == null) throw new Error("上传接口未返回文件 ID");
            setForm(current => ({
                ...current,
                certificateFiles: { ...current.certificateFiles, shipInspectionReportFile: fileId }
            }));
        } catch {
            setError("船舶检验报告上传失败");
        } finally {
            setRecognizing("");
        }
    };

    /** 识别船检报告，成功结果覆盖对应船舶尺寸字段。 */
    const handleInspectionRecognize = async () => {
        const fileId = formRef.current.certificateFiles?.shipInspectionReportFile;
        if (fileId == null) {
            if (loading) {
                window.setTimeout(() => void handleInspectionRecognize(), 0);
                return;
            }
            setError("请先上传船舶检验报告");
            return;
        }
        setError("");
        setRecognizing("inspection");
        try {
            const result = await recognizeInspectionReport([fileId]);
            setForm(current => ({
                ...current,
                length: result.len ?? current.length,
                width: result.wid ?? current.width,
                deep: result.deep ?? current.deep
            }));
        } catch {
            setError("船舶检验报告识别失败");
        } finally {
            setRecognizing("");
        }
    };

    /** 识别身份证指定面，并将 OCR 结果合并回 Vue idCardInfo。 */
    const handleIdCardRecognize = async (isFront: boolean) => {
        const currentForm = formRef.current;
        const fileId = isFront
            ? currentForm.certificateFiles?.captainsIdCardFrontFile
            : currentForm.certificateFiles?.captainsIdCardBackFile;
        if (fileId == null) {
            if (loading) {
                window.setTimeout(() => void handleIdCardRecognize(isFront), 0);
                return;
            }
            setError(`请先上传身份证${isFront ? "正面" : "反面"}`);
            return;
        }
        setError("");
        setRecognizing(isFront ? "id-card-front" : "id-card-back");
        try {
            const result = await recognizeIdCard(fileId, isFront ? 1 : 0);
            setForm(current => {
                const nextInfo = { ...current.idCardInfo };
                if (isFront) {
                    const validPeriod = result.idCardBackInfo?.validPeriod || "";
                    const periodMatch = validPeriod.match(/^(\d{4}-\d{2}-\d{2})-(.+)$/);
                    const start = periodMatch?.[1];
                    const end = periodMatch?.[2];
                    nextInfo.idCardStartDate = start || nextInfo.idCardStartDate;
                    nextInfo.idCardEndDate = (end === "长期" ? "2099-12-31" : end) || nextInfo.idCardEndDate;
                } else {
                    const faceInfo = result.idCardFaceInfo;
                    nextInfo.name = faceInfo?.name || nextInfo.name;
                    nextInfo.sex = faceInfo?.sex || nextInfo.sex;
                    nextInfo.birthDate = formatChineseDate(faceInfo?.birthDate) || nextInfo.birthDate;
                    nextInfo.address = faceInfo?.address || nextInfo.address;
                    nextInfo.idNumber = faceInfo?.idNumber || nextInfo.idNumber;
                }
                const nextForm = { ...current, idCardInfo: nextInfo };
                formRef.current = nextForm;
                return nextForm;
            });
        } catch {
            setError(`身份证${isFront ? "正面" : "反面"}识别失败`);
        } finally {
            setRecognizing("");
        }
    };

    /** 兼容身份证 OCR 返回的中文出生日期格式。 */
    const formatChineseDate = (value?: string) => {
        if (!value) return "";
        const match = value.match(/(\d{4})年(\d{1,2})月(\d{1,2})日/);
        return match ? `${match[1]}-${match[2].padStart(2, "0")}-${match[3].padStart(2, "0")}` : value;
    };

    /** 识别船员适任证书；旧接口返回“无”时按 Vue 规则降级为普通船员。 */
    const handleCrewRecognize = async () => {
        const fileId = formRef.current.certificateFiles?.competencyCertificateFile?.[0];
        if (fileId == null) {
            if (loading) {
                window.setTimeout(() => void handleCrewRecognize(), 0);
                return;
            }
            setError("请先上传船员适任证书");
            return;
        }
        setError("");
        setRecognizing("crew");
        try {
            const result = await recognizeCrewCertificate(fileId);
            const nextForm = { ...formRef.current, jobQualification: result === "无" ? "普通船员" : result };
            formRef.current = nextForm;
            setForm(nextForm);
        } catch {
            setError("船员证书识别失败");
        } finally {
            setRecognizing("");
        }
    };

    /** 识别船舶营业运输证，供资质区复用 Vue 既有能力。 */
    const handleBusinessTransportRecognize = async () => {
        const fileId = formRef.current.certificateFiles?.shipBusinessTransportCertificateFile;
        if (fileId == null) {
            setError("请先上传船舶营业运输证");
            return;
        }
        setError("");
        setRecognizing("business-transport");
        try {
            const result = await recognizeBusinessTransport(fileId);
            setForm(current => {
                const nextForm = {
                    ...current,
                    tonTotal: typeof result.shipTon === "number" ? result.shipTon : current.tonTotal,
                    tonCapacity: typeof result.deadWeightTon === "number" ? result.deadWeightTon : current.tonCapacity
                };
                formRef.current = nextForm;
                return nextForm;
            });
        } catch {
            setError("船舶营业运输证识别失败");
        } finally {
            setRecognizing("");
        }
    };

    /** 沿用 Vue 编辑/绑定分支提交策略，并在成功后回到详情页。 */
    const submit = async () => {
        if (!id) return;
        if (!bindMode && !form.cnname?.trim()) {
            setError("请输入中文船名");
            return;
        }
        if (bindMode && !Number(form.carrier)) {
            setError("请选择承运商");
            return;
        }
        setSubmitting(true);
        setError("");
        try {
            if (bindMode) {
                await bindShip(id, {
                    carrier: Number(form.carrier),
                    tonTotal: form.tonTotal,
                    tonCapacity: form.tonCapacity,
                    tonPure: form.tonPure
                });
            } else {
                await updateShip(id, form);
            }
            navigate(`/logistics/shipManage/shipInfo/shipInfoDetail/${encodeURIComponent(id)}`);
        } catch {
            setError(bindMode ? "船舶绑定失败" : "船舶信息保存失败");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section aria-label={bindMode ? "ship-info-bind-page" : "ship-info-edit-page"}>
            <Card aria-busy={loading}>
                <Typography.Title level={4}>{bindMode ? "绑定船舶" : "修改船舶信息"}</Typography.Title>
                {error ? <Alert title={error} role="alert" type="error" /> : null}
                {/* 对齐旧 Vue shipInfoEdit 的四列基础信息网格，保留现有字段顺序和交互。 */}
                <ZhGrid data-testid="ship-info-edit-basic-grid" cols={4} gap={[40, 2]}>
                    <ZhGridItem>
                        <label>
                            中文船名
                            <Input
                                aria-label="中文船名"
                                disabled={bindMode}
                                value={form.cnname || ""}
                                placeholder="请输入中文船名"
                                onChange={event => setField("cnname", event.target.value)}
                            />
                        </label>
                    </ZhGridItem>
                    <ZhGridItem>
                        <label>
                            英文船名
                            <Input
                                aria-label="英文船名"
                                disabled={bindMode}
                                value={form.name || ""}
                                onChange={event => setField("name", event.target.value)}
                            />
                        </label>
                    </ZhGridItem>
                    <ZhGridItem>
                        <label>
                            船长
                            <Input
                                aria-label="船长"
                                disabled={bindMode}
                                value={form.length ?? ""}
                                onChange={event => setField("length", Number(event.target.value) || undefined)}
                            />
                        </label>
                    </ZhGridItem>
                    <ZhGridItem>
                        <label>
                            船宽
                            <Input
                                aria-label="船宽"
                                disabled={bindMode}
                                value={form.width ?? ""}
                                onChange={event => setField("width", Number(event.target.value) || undefined)}
                            />
                        </label>
                    </ZhGridItem>
                    <ZhGridItem>
                        <label>
                            型深
                            <Input
                                aria-label="型深"
                                disabled={bindMode}
                                value={form.deep ?? ""}
                                onChange={event => setField("deep", Number(event.target.value) || undefined)}
                            />
                        </label>
                    </ZhGridItem>
                    <ZhGridItem>
                        <label>
                            承运商
                            <Input
                                aria-label="承运商"
                                value={form.carrier ?? ""}
                                onChange={event => setField("carrier", Number(event.target.value) || undefined)}
                            />
                        </label>
                    </ZhGridItem>
                    <ZhGridItem>
                        <label>
                            总吨
                            <Input
                                aria-label="总吨"
                                value={form.tonTotal ?? ""}
                                onChange={event => setField("tonTotal", Number(event.target.value) || undefined)}
                            />
                        </label>
                    </ZhGridItem>
                    <ZhGridItem>
                        <label>
                            载重吨
                            <Input
                                aria-label="载重吨"
                                value={form.tonCapacity ?? ""}
                                onChange={event => setField("tonCapacity", Number(event.target.value) || undefined)}
                            />
                        </label>
                    </ZhGridItem>
                    <ZhGridItem>
                        <label>
                            净吨
                            <Input
                                aria-label="净吨"
                                value={form.tonPure ?? ""}
                                onChange={event => setField("tonPure", Number(event.target.value) || undefined)}
                            />
                        </label>
                    </ZhGridItem>
                </ZhGrid>
                {!bindMode ? (
                    <Card className="mt-6" size="small" title="船舶资质">
                        {/* 资质识别操作同样按旧 Vue 的四列 Grid 组织，不改变各操作的状态逻辑。 */}
                        <ZhGrid data-testid="ship-info-edit-certificate-grid" cols={4} gap={[40, 2]}>
                            <ZhGridItem>
                                <label>
                                    船舶检验报告上传
                                    <input
                                        aria-label="船舶检验报告上传"
                                        type="file"
                                        onChange={event => void handleInspectionUpload(event.target.files?.[0])}
                                    />
                                </label>
                                <div>
                                    当前文件 ID：
                                    <span>{form.certificateFiles?.shipInspectionReportFile ?? "暂无"}</span>
                                </div>
                                <Button
                                    autoInsertSpace={false}
                                    loading={recognizing === "inspection"}
                                    onClick={() => void handleInspectionRecognize()}
                                >
                                    识别船检报告
                                </Button>
                            </ZhGridItem>
                            <ZhGridItem>
                                <div>
                                    船舶营业运输证文件 ID：
                                    <span>{form.certificateFiles?.shipBusinessTransportCertificateFile ?? "暂无"}</span>
                                </div>
                                <Button
                                    autoInsertSpace={false}
                                    loading={recognizing === "business-transport"}
                                    onClick={() => void handleBusinessTransportRecognize()}
                                >
                                    识别船舶营业运输证
                                </Button>
                            </ZhGridItem>
                            <ZhGridItem>
                                <div>
                                    身份证正面文件 ID：
                                    <span>{form.certificateFiles?.captainsIdCardFrontFile ?? "暂无"}</span>
                                </div>
                                <Button
                                    autoInsertSpace={false}
                                    loading={recognizing === "id-card-front"}
                                    onClick={() => void handleIdCardRecognize(true)}
                                >
                                    识别身份证正面
                                </Button>
                                <div>
                                    身份证有效期：<span>{form.idCardInfo?.idCardStartDate || "暂无"}</span> -{" "}
                                    <span>{form.idCardInfo?.idCardEndDate || "暂无"}</span>
                                </div>
                            </ZhGridItem>
                            <ZhGridItem>
                                <div>
                                    身份证反面文件 ID：
                                    <span>{form.certificateFiles?.captainsIdCardBackFile ?? "暂无"}</span>
                                </div>
                                <Button
                                    autoInsertSpace={false}
                                    loading={recognizing === "id-card-back"}
                                    onClick={() => void handleIdCardRecognize(false)}
                                >
                                    识别身份证反面
                                </Button>
                                <div>
                                    姓名：<span>{form.idCardInfo?.name || "暂无"}</span>
                                </div>
                                <div>
                                    性别：<span>{form.idCardInfo?.sex || "暂无"}</span>
                                </div>
                                <div>
                                    出生日期：<span>{form.idCardInfo?.birthDate || "暂无"}</span>
                                </div>
                                <div>
                                    住址：<span>{form.idCardInfo?.address || "暂无"}</span>
                                </div>
                                <div>
                                    身份证号：<span>{form.idCardInfo?.idNumber || "暂无"}</span>
                                </div>
                            </ZhGridItem>
                            <ZhGridItem>
                                <div>
                                    船员适任证书文件 ID：
                                    <span>{form.certificateFiles?.competencyCertificateFile?.[0] ?? "暂无"}</span>
                                </div>
                                <Button
                                    autoInsertSpace={false}
                                    loading={recognizing === "crew"}
                                    onClick={() => void handleCrewRecognize()}
                                >
                                    识别船员证书
                                </Button>
                                <div>
                                    职务资格：<span>{form.jobQualification || "暂无"}</span>
                                </div>
                            </ZhGridItem>
                        </ZhGrid>
                    </Card>
                ) : null}
                <Space className="mt-6">
                    <Button autoInsertSpace={false} type="primary" loading={submitting} onClick={() => void submit()}>
                        提交
                    </Button>
                    <Button autoInsertSpace={false} onClick={() => navigate(-1)}>
                        返回
                    </Button>
                </Space>
            </Card>
        </section>
    );
}

export default ShipInfoEditPage;
